import i18n from "@/i18n";

export const validationRules = (translateFunction: Function): Rules => ({
  required: (value: string) => !!value || translateFunction('auth.required.default'),
  requiredWithMessageInEnglish: (value: string) => !!value || 'Required',
  name: (value: string) => {
    const pattern = /^[a-zA-Z0-9\-]+$/;
    return pattern.test(value) || translateFunction('auth.required.name');
  },
  url: (value: string) => {
    const pattern = /^[a-zA-Z0-9\-]+$/;
    return pattern.test(value) || translateFunction('auth.required.url');
  },
  email: (value: string) => {
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(value) || translateFunction('auth.required.email');
  },
  number: (value: string) => {
    const pattern = /^[0-9]+$/;
    return pattern.test(value) || translateFunction('auth.required.number');
  },
  password: {
    lowercase: (value: string) => {
      const pattern = /[a-z]+/;
      return pattern.test(value) || translateFunction('auth.required.passwordRules.lowercase');
    },
    uppercase: (value: string) => {
      const pattern = /[A-Z]+/;
      return pattern.test(value) || translateFunction('auth.required.passwordRules.uppercase');
    },
    length: (value: string) => {
      const pattern = /.{8,}/;
      return pattern.test(value) || translateFunction('auth.required.passwordRules.length');
    },
    number: (value: string) => {
      const pattern = /[0-9]+/;
      return pattern.test(value) || translateFunction('auth.required.passwordRules.number');
    },
    special: (value: string) => {
      const pattern = /\W|_/g;
      return pattern.test(value) || translateFunction('auth.required.passwordRules.special');
    },
  },
});

export const englishValidationRules = (): Rules => ({
  required: (value: string) => !!value || 'Required',
  requiredWithMessageInEnglish: (value: string) => !!value || 'Required',
  name: (value: string) => {
    const pattern = /^[a-zA-Z0-9\-]+$/;
    return pattern.test(value) || 'Invalid name';
  },
  url: (value: string) => {
    const pattern = /^[a-zA-Z0-9\-]+$/;
    return pattern.test(value) || 'Invalid url';
  },
  email: (value: string) => {
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(value) || 'Invalid email';
  },
  number: (value: string) => {
    const pattern = /^[0-9]+$/;
    return pattern.test(value) || 'Only numbers allowed';
  },
  password: {
    lowercase: (value: string) => {
      const pattern = /[a-z]+/;
      return pattern.test(value) || 'One lowercase letter required';
    },
    uppercase: (value: string) => {
      const pattern = /[A-Z]+/;
      return pattern.test(value) || 'One uppercase letter required';
    },
    length: (value: string) => {
      const pattern = /.{8,}/;
      return pattern.test(value) || '8 characters minimum';
    },
    number: (value: string) => {
      const pattern = /[0-9]+/;
      return pattern.test(value) || 'One number required';
    },
    special: (value: string) => {
      const pattern = /\W|_/g;
      return pattern.test(value) || 'One special character required';
    },
  }
});

const getTranslation = (key: string) => i18n.t(key);

export const Rules = validationRules(getTranslation);

export const EnglishRules = englishValidationRules();

export type ValidationRuleFunction = (value: string) => true | string;

export interface Rules {
  required: ValidationRuleFunction;
  requiredWithMessageInEnglish: ValidationRuleFunction;
  name: ValidationRuleFunction;
  url: ValidationRuleFunction;
  email: ValidationRuleFunction;
  number: ValidationRuleFunction;
  password: Password;
}

interface Password {
  [key: string]: ValidationRuleFunction;
}
